import React, { useState } from "react";
import {
  Avatar,
  AvatarGroup,
  Box,
  Button,
  Link,
  Popover,
  Stack,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/authContext";
import { getCountryNameFromCode } from "../utils/utils";

const TMDB_IMAGE_BASE = "https://image.tmdb.org/t/p/w92";

export default function WatchProviders({
  providers,
  checked,
  isMobile = false,
}) {
  const navigate = useNavigate();
  const { user, openAuthDialog } = useAuth();
  const [anchorEl, setAnchorEl] = useState(null);

  // Only display nothing if we haven't yet checked the data
  if (!checked) return <Box />;

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleEditClick = () => {
    if (!user) {
      openAuthDialog("Changing your region");
      return;
    }
    navigate("/settings/services");
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const visibleProviders = providers.slice(0, 2);
  const remainingCount = providers.length - 2;

  return (
    <>
      {!providers.length ? (
        <Button
          variant="outlined"
          onClick={handleOpen}
          size="large"
          sx={{
            p: 0.5,
            height: isMobile ? undefined : "38px",
            width: isMobile ? "100%" : undefined,
            color: "text.disabled",
            borderColor: "text.disabled",
            "&:hover": {
              borderColor: "text.disabled",
            },
            fontSize: "0.7rem",
            lineHeight: 1.2,
          }}
        >
          Streaming <br />
          unavailable
        </Button>
      ) : (
        <Box
          onClick={handleOpen}
          sx={{ cursor: "pointer", display: "inline-flex" }}
        >
          <AvatarGroup max={3}>
            {visibleProviders.map((provider) => (
              <Avatar
                key={provider.provider_id}
                src={`${TMDB_IMAGE_BASE}${provider.logo_path}`}
                alt={provider.name}
                sx={{ width: isMobile ? 25 : 30, height: isMobile ? 25 : 30 }}
              />
            ))}

            {remainingCount > 0 && (
              <Avatar
                sx={{
                  width: isMobile ? 25 : 30,
                  height: isMobile ? 25 : 30,
                  fontSize: isMobile ? "0.75rem" : "1rem",
                }}
              >
                <span style={{ marginLeft: 3 }}>+{remainingCount}</span>
              </Avatar>
            )}
          </AvatarGroup>
        </Box>
      )}

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        sx={{ mt: 1 }}
      >
        <Box
          sx={{
            p: 2,
            maxWidth: 250,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Typography variant="body2" sx={{ mb: 1 }}>
            {!providers.length
              ? "Not streaming in your region"
              : "Currently streaming on:"}
          </Typography>

          {!!providers.length && (
            <Stack spacing={1} sx={{ mb: 1 }}>
              {providers.map((provider) => (
                <Stack
                  key={provider.provider_id}
                  direction="row"
                  alignItems="center"
                  spacing={1}
                >
                  <Avatar
                    src={`${TMDB_IMAGE_BASE}${provider.logo_path}`}
                    alt={provider.name}
                    sx={{ width: 28, height: 28 }}
                  />
                  <Typography variant="body2">{provider.name}</Typography>
                </Stack>
              ))}
            </Stack>
          )}

          <Typography variant="caption" color="text.secondary">
            Region: {getCountryNameFromCode(user?.preferences?.region || "GB")}{" "}
            (
            <Link onClick={handleEditClick} color="text.secondary" sx={{ cursor: "pointer" }}>
              edit
            </Link>
            )
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Data provided by{" "}
            <Link
              href="https://justwatch.com"
              target="_blank"
              rel="noopener noreferrer"
              color="text.secondary"
            >
              JustWatch
            </Link>
          </Typography>
        </Box>
      </Popover>
    </>
  );
}
